import { NcwOnboardingRecoveryImage, useIntl } from '@capsicum/common';
import { Alert, BaseTextInput, Button, ButtonSection, Image, Text, useRouter } from '@capsicum/common-web';
import { ShieldLockIcon } from '@capsicum/common-web/icons';
import { type ReactNode, useCallback } from 'react';
import styled from 'styled-components';

import { useNCGenerateRecoveryKitWeb } from '../../hooks';
import { useRecoveryKitGenerationUtils } from '../../hooks/recovery-kit';

export const NCGenerateRecoveryKit = () => {
	const { replace, searchParams } = useRouter();
	const { $t } = useIntl();
	const isNewUser = searchParams?.get('isNewUser');
	const { code, generateRecoveryKit, intlTexts, keyId, loading } = useNCGenerateRecoveryKitWeb();

	const { handleClickDownload, recoveryCodeCopyIcon, recoveryKeyIdCopyIcon } = useRecoveryKitGenerationUtils(
		{
			code,
			keyId
		},
		isNewUser === 'Y'
	);

	const isGenerated = code && keyId;

	const handleClickButton = useCallback(() => {
		if (isGenerated) {
			replace('/settings');
		} else {
			generateRecoveryKit();
		}
	}, [isGenerated]);

	return (
		<S.Container>
			<S.HeaderContainer>
				<S.ImageContainer>
					<S.StyledImage alt="Generate Recovery" height={132} src={NcwOnboardingRecoveryImage} />
				</S.ImageContainer>
				<S.Title>{isGenerated ? intlTexts.generatedTitle : intlTexts.title}</S.Title>
				{!isGenerated && (
					<S.SubtitleContainer>
						<S.Subtitle>
							{$t('ncOnboarding.recoveryKit.generateRecoveryKit.explanation1', {
								strong: (chunks: Array<ReactNode>) => <S.Bold>{chunks}</S.Bold>
							})}
						</S.Subtitle>
						<S.Subtitle>
							{$t('ncOnboarding.recoveryKit.generateRecoveryKit.explanation2', {
								strong: (chunks: Array<ReactNode>) => <S.Bold>{chunks}</S.Bold>
							})}
						</S.Subtitle>
						<S.Subtitle>
							{$t('ncOnboarding.recoveryKit.generateRecoveryKit.explanation3', {
								strong: (chunks: Array<ReactNode>) => <S.Bold>{chunks}</S.Bold>
							})}
						</S.Subtitle>
					</S.SubtitleContainer>
				)}
			</S.HeaderContainer>
			{isGenerated && (
				<S.BodyContainer>
					<S.TextInputContainer>
						<S.TextInput
							iconEnd={recoveryCodeCopyIcon}
							label={intlTexts.inputRecoveryCode}
							readOnly
							testID="ncOnboarding-recoveryCode-input"
							value={code}
						/>
						<S.TextInput
							iconEnd={recoveryKeyIdCopyIcon}
							label={intlTexts.inputRecoveryKeyId}
							readOnly
							testID="ncOnboarding-recoveryKeyId-input"
							value={keyId}
						/>
					</S.TextInputContainer>
					<S.FullWidthAlert
						closable={false}
						icon={ShieldLockIcon}
						subTitle={intlTexts.alertSubtitle}
						testID="ncOnboarding-recoveryKit-alert"
						title={intlTexts.alertTitle}
						type="important"
					/>
					<Button
						color="brand"
						fullWidth
						testID="ncOnboarding-downloadRecoveryKit-button"
						text={intlTexts.buttonDownload}
						variant="outlined"
						onClick={handleClickDownload}
					>
						{intlTexts.buttonDownload}
					</Button>
				</S.BodyContainer>
			)}
			<ButtonSection>
				<Button
					color="brand"
					fullWidth
					isLoading={loading}
					testID="ncGenerateRecoveryKit-confirmDrawerAccept-button"
					text={isGenerated ? intlTexts.buttonContinue : intlTexts.buttonGenerate}
					variant="default"
					onClick={handleClickButton}
				/>
			</ButtonSection>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div`
		width: 100%;
		display: flex;
		flex-flow: column;
		align-items: center;
		padding: ${({ theme }) => theme.spacings.small};
		padding-bottom: ${({ theme }) => theme.spacings.xxxlarge};
	`;

	export const HeaderContainer = styled.div`
		display: flex;
		width: 100%;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		row-gap: ${({ theme }) => theme.spacings.medium};
	`;

	export const Title = styled.div`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		text-align: center;
	`;

	export const SubtitleContainer = styled.div`
		display: flex;
		flex-direction: column;
		row-gap: ${({ theme }) => theme.spacings.medium};
		margin-top: ${({ theme }) => theme.spacings.xxlarge};
	`;

	export const Subtitle = styled.div`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin-bottom: ${({ theme }) => theme.spacings.large};
		text-align: center;
	`;

	export const SubtitleRed = styled.div`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.destructive.enabled};
		text-align: center;
	`;

	export const BodyContainer = styled.div`
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: center;
		row-gap: ${({ theme }) => theme.spacings.large};
		margin-top: ${({ theme }) => theme.spacings.xxlarge};
		margin-bottom: ${({ theme }) => theme.spacings.xxlarge};
	`;

	export const ImageContainer = styled.div`
		width: 100%;
		max-width: 100px;
		display: flex;
		justify-content: center;
		align-items: center;
	`;

	export const StyledImage = styled(Image)`
		width: 100%;
		object-fit: contain;
	`;

	export const TextInputContainer = styled.div`
		display: flex;
		width: 100%;
		flex-direction: column;
		justify-content: center;
	`;

	export const TextInput = styled(BaseTextInput)`
		width: 100%;

		> div,
		> div:hover,
		> div:focus-within {
			background: ${({ theme }) => theme.colors.backgrounds.main.container};
			border: none;
		}
	`;
	export const FormContainer = styled.div`
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		padding: ${({ theme }) => `${theme.spacings.medium} 0`};
		width: 100%;
	`;

	export const CheckboxesContainer = styled.div`
		display: flex;
		flex-direction: column;
		row-gap: ${({ theme }) => theme.spacings.small};

		padding: ${({ theme }) => `0 ${theme.spacings.xlarge}`};
	`;

	export const CheckboxInputText = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main};
	`;

	export const Bold = styled.span`
		font-weight: bold;
	`;

	export const FullWidthAlert = styled(Alert)`
		width: 100%;
	`;
}
